import { useEffect, useState } from 'react';

import { getWorkspaces } from 'services/workspaceService';

const WORKSPACE_STORAGE_KEY = 'selected_workspace_id';

// Module-level shared state — all hook instances share one source of truth
let sharedWorkspaces = [];
let sharedSelectedId = (() => {
  try {
    const item = localStorage.getItem(WORKSPACE_STORAGE_KEY);

    return item ? JSON.parse(item) : null;
  }
  catch {
    return null;
  }
})();

const workspaceListeners = new Set();
const idListeners = new Set();
let workspacesFetched = false;
let isFetching = false;

const notifyWorkspaceListeners = ws => workspaceListeners.forEach(fn => fn(ws));
const notifyIdListeners = id => idListeners.forEach(fn => fn(id));

const setSharedId = id => {
  sharedSelectedId = id;

  try {
    localStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(id));
  }
  catch { /* ignore */ }

  notifyIdListeners(id);
};

const loadWorkspaces = (attemptsLeft = 2) => {
  if (isFetching) {
    return;
  }

  isFetching = true;

  getWorkspaces()
    .then(data => {
      workspacesFetched = true;
      isFetching = false;
      sharedWorkspaces = data;
      notifyWorkspaceListeners(data);

      if (!sharedSelectedId || !data.find(w => w.uuid === sharedSelectedId)) {
        const defaultWorkspace = data.find(w => w.is_default) ?? data[0];

        if (defaultWorkspace) {
          setSharedId(defaultWorkspace.uuid);
        }
      }
    })
    .catch(() => {
      isFetching = false;

      if (attemptsLeft > 0) {
        loadWorkspaces(attemptsLeft - 1);
      }
      else {
        // All retries exhausted — unblock components waiting on isLoadingWorkspaces
        workspacesFetched = true;
        notifyWorkspaceListeners(sharedWorkspaces);
      }
    });
};

export const reloadWorkspaces = () => {
  workspacesFetched = false;
  isFetching = false;
  loadWorkspaces();
};

export const resetWorkspaceState = () => {
  sharedWorkspaces = [];
  sharedSelectedId = null;
  workspacesFetched = false;
  isFetching = false;

  try {
    localStorage.removeItem(WORKSPACE_STORAGE_KEY);
  }
  catch { /* ignore */ }

  notifyWorkspaceListeners([]);
  notifyIdListeners(null);
};

export const useWorkspace = () => {
  const [workspaces, setWorkspaces] = useState(sharedWorkspaces);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(sharedSelectedId);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(!workspacesFetched);

  useEffect(() => {
    const handleWorkspaces = ws => {
      setWorkspaces(ws);
      setIsLoadingWorkspaces(false);
    };

    workspaceListeners.add(handleWorkspaces);
    idListeners.add(setSelectedWorkspaceId);

    return () => {
      workspaceListeners.delete(handleWorkspaces);
      idListeners.delete(setSelectedWorkspaceId);
    };
  }, []);

  useEffect(() => {
    if (!workspacesFetched) {
      try {
        const userObject = JSON.parse(localStorage.getItem('userObject'));

        if (userObject?.access) {
          loadWorkspaces();
        }
        else {
          setIsLoadingWorkspaces(false);
        }
      }
      catch {
        setIsLoadingWorkspaces(false);
      }
    }
  }, []);

  const selectedWorkspace = workspaces.find(w => w.uuid === selectedWorkspaceId)
    ?? workspaces.find(w => w.is_default)
    ?? workspaces[0]
    ?? null;

  const selectWorkspace = uuid => setSharedId(uuid);
  const role = selectedWorkspace?.role ?? null;
  const isViewer = role === 'viewer';
  const isOwner = role === 'owner';

  return { workspaces, selectedWorkspace, selectWorkspace, role, isViewer, isOwner, isLoadingWorkspaces };
};
